import { z } from 'zod';
import { catalogIngredientSchema } from './catalog.ts';
import {
  cookSessionSchema,
  manualItemSchema,
  mealSlotSchema,
  planMemberSchema,
  planEntrySchema,
  shoppingCheckSchema,
  shoppingPrefsSchema,
  shoppingRecipeSchema,
} from './planning.ts';
import { ingredientLineSchema, recipeSchema } from './recipes.ts';

/** `GET /api/sync?since=<cursor>` (PLAN.md §9, ADR 0006). */
export const syncQuerySchema = z.object({
  since: z.coerce.number().int().min(0).default(0),
});

/** A recipe as mirrored on the device: its lines and catalog entries travel separately. */
export const syncRecipeSchema = recipeSchema.omit({ ingredients: true, catalog: true });
export type SyncRecipe = z.infer<typeof syncRecipeSchema>;

export const syncLineSchema = ingredientLineSchema.extend({ recipeId: z.uuid() });
export type SyncLine = z.infer<typeof syncLineSchema>;

export const syncResponseSchema = z.object({
  /** Pass back as `since`; rows up to it are included. */
  cursor: z.number().int().min(0),
  /** More changes wait: pull again right away. */
  more: z.boolean(),
  /** The cursor is from another database (restore, new server): drop the mirror and pull from 0. */
  reset: z.boolean(),
  recipes: z.array(syncRecipeSchema),
  lines: z.array(syncLineSchema),
  /** Catalog entries with this household's view; re-sent when the household's override changes. */
  ingredients: z.array(catalogIngredientSchema),
  slots: z.array(mealSlotSchema),
  cookSessions: z.array(cookSessionSchema),
  planEntries: z.array(planEntrySchema),
  shoppingChecks: z.array(shoppingCheckSchema),
  manualItems: z.array(manualItemSchema),
  shoppingRecipes: z.array(shoppingRecipeSchema),
  members: z.array(planMemberSchema),
  /** The household's shopping preferences (at most one). */
  shoppingPrefs: z.array(shoppingPrefsSchema),
  /** Tombstones: ids to drop. */
  deleted: z.object({
    recipes: z.array(z.uuid()),
    lines: z.array(z.uuid()),
    ingredients: z.array(z.uuid()),
    slots: z.array(z.uuid()),
    cookSessions: z.array(z.uuid()),
    planEntries: z.array(z.uuid()),
    /** `shoppingCheckId`s. */
    shoppingChecks: z.array(z.string()),
    manualItems: z.array(z.uuid()),
    shoppingRecipes: z.array(z.uuid()),
    members: z.array(z.uuid()),
  }),
});
export type SyncResponse = z.infer<typeof syncResponseSchema>;
